import { Typography, styled } from 'opensolar-ui'
import { BaseThemeType, useMediaQuery } from 'opensolar-ui/src/theme'
import { useMemo } from 'react'
import { MarketplaceQuotationTableBreakdownItem } from '../types'
import { formatCurrencyWithSymbol } from '../utils'

type PropTypes = {
  tableRow: MarketplaceQuotationTableBreakdownItem
  currencySymbol: string
}

const Row = styled('div')<{ highlight: boolean }>(
  ({ theme, highlight }: { theme: BaseThemeType; highlight: boolean }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    color: theme.palette.grey[800],
    fontWeight: highlight ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
    marginBottom: '4px',
  })
)

const Left = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flex: 1,
})

const Right = styled('div')<{ highlight: boolean }>(
  ({ theme, highlight }: { theme: BaseThemeType; highlight: boolean }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    flex: 1,
    color: theme.palette.grey[800],
    fontWeight: highlight ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  })
)

const MainDisplay = styled(Typography)<{ highlight: boolean }>(
  ({ theme, highlight }: { theme: BaseThemeType; highlight: boolean }) => ({
    color: theme.palette.grey[700],
    fontWeight: highlight ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  })
)

const ExtraDisplay = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.grey[700],
  fontWeight: theme.typography.fontWeightRegular,
}))

const formatTermValue = (value: number): string => {
  const years = Math.floor(value / 12)
  const yearsSuffix = years === 1 ? 'year' : 'years'
  const months = value % 12
  const monthsSuffix = months === 1 ? 'month' : 'months'

  return months === 0 ? `${years} ${yearsSuffix}` : `${years} ${yearsSuffix} ${months} ${monthsSuffix}`
}

const formatValue = (
  value: string | number,
  valueFormat: 'string' | 'number' | 'currency' | 'percentage' | 'term' | null,
  currencySymbol: string,
  significantDigits: number | null
): string => {
  const places = typeof significantDigits === 'number' ? significantDigits : 0

  if (valueFormat === 'currency') {
    return formatCurrencyWithSymbol(value, currencySymbol, undefined, places)
  } else if (valueFormat === 'number') {
    return Number(value).toFixed(places)
  } else if (valueFormat === 'percentage') {
    return `${Number(value).toFixed(places)}%`
  } else if (valueFormat === 'term') {
    return formatTermValue(Number(value))
  }

  return String(value)
}

const FinanceMarketplaceBreakdownTableRow = (props: PropTypes) => {
  const { tableRow, currencySymbol } = props
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  const formattedValue = useMemo(
    () => formatValue(tableRow.value ?? '', tableRow.value_format, currencySymbol, tableRow.significant_digits),
    [tableRow]
  )

  return (
    <Row highlight={tableRow.highlight_value || false}>
      <Left>
        <MainDisplay highlight={tableRow.highlight_label || false} textVariant={isMobile ? 'body1' : 'body2'}>
          {tableRow.label}
        </MainDisplay>
        {tableRow.label_extra && <ExtraDisplay textVariant="caption1">{tableRow.label_extra}</ExtraDisplay>}
      </Left>
      <Right highlight={tableRow.highlight_value || false}>
        <MainDisplay highlight={tableRow.highlight_value || false} textVariant={isMobile ? 'body1' : 'body2'}>
          {formattedValue}
        </MainDisplay>
        {tableRow.value_extra && <ExtraDisplay textVariant="caption1">{tableRow.value_extra}</ExtraDisplay>}
      </Right>
    </Row>
  )
}

export default FinanceMarketplaceBreakdownTableRow
